import client, {QueryOptions} from "@irati/utils/services/apollo"
import {WellnessHomepage, WellnessMedia, WellnessMediaCategory, WellnessMediaList, WellnessLane, WellnessCarousel} from "@irati/types"
import PBService from "@irati/utils/services"
import authentication from "@irati/auth"

import getWellnessHomepageQuery, {getWellnessHomepageKey} from "./queries/getWellnessHomepage"
import getLanesQuery, {getLanesKey} from "./queries/getWellnessLanes"
import getWellnessMediaByIdQuery, {getWellnessMediaByIdKey} from "./queries/getWellnessMediaById"
import getRelatedWellnessMediaListQuery, {getRelatedWellnessMediaListKey} from "./queries/getRelatedWellnessMediaList"
import getWellnessMediaCategoriesQuery, {getWellnessMediaCategoriesKey} from "./queries/getWellnessMediaCategories"
import getMediaByCategoryIdQuery, {getMediaByCategoryIdKey} from "./queries/getMediaByCategoryId"
import getWellnessCategoryTagsQuery, {getWellnessMediaCategoryTagsKey} from "./queries/getWellnessCategoryTags"
import getMediaByTagNameKeyQuery, {getMediaByTagNameKey} from "./queries/getMediaByTagName"
import wellnessMediaList, {wellnessMediaListQueryKey} from "./queries/wellnessMediaList"
import getWellnessVideoSourceQuery, {getWellnessVideoSourceByIdKey} from "./queries/getVimeoVideoSource"
import getBannerQuery, {getBannerKey} from "./queries/getBanner"
import getCarouselQuery, {getCarouselKey} from "./queries/getCarousel"
import getWellnessCategoryById, {getWellnessCategoryByIdKey} from "./queries/getWellnessCategoryById"
import createMediaRental, {createMediaRentalKey} from "./mutations/createMediaRental"
import wellnessMediaRentalsQuery, {wellnessMediaRentalsKey} from "./queries/wellnessMediaRentals"
import completeMediaRental, {completeMediaRentalKey} from "./mutations/completeMediaRental"

interface MediaListQueryInterface {
  offset?: number
  limit?: number
  categoryId?: string
  tags?: string[]
}

class WellnessService extends PBService {
  getHomepage(options: QueryOptions = {}): Promise<WellnessHomepage> {
    return client
      .query({
        ...options,
        query: getWellnessHomepageQuery
      })
      .then(({data}) => data[getWellnessHomepageKey])
  }

  getCarousel(options: QueryOptions = {}): Promise<WellnessCarousel[]> {
    return client.query({fetchPolicy: "cache-first", ...options, query: getCarouselQuery}).then(({data}) => data[getCarouselKey])
  }

  getLanes(options: QueryOptions = {}): Promise<WellnessLane[]> {
    return client.query({fetchPolicy: "cache-first", ...options, query: getLanesQuery}).then(({data}) => data[getLanesKey])
  }

  getMediaById(mediaId: string, options: QueryOptions = {}): Promise<WellnessMedia> {
    return client
      .query({
        ...options,
        query: getWellnessMediaByIdQuery,
        variables: {
          mediaId
        }
      })
      .then(({data}) => data[getWellnessMediaByIdKey])
  }

  getRelatedMediaList(mediaId: string, options: QueryOptions = {}): Promise<WellnessMedia[]> {
    return client
      .query({
        ...options,
        query: getRelatedWellnessMediaListQuery,
        variables: {
          mediaId
        }
      })
      .then(({data}) => data[getRelatedWellnessMediaListKey])
  }

  getWellnessCategoryById(categoryId: string, options: QueryOptions = {}): Promise<WellnessMediaCategory> {
    return client
      .query({
        ...options,
        query: getWellnessCategoryById,
        variables: {
          categoryId
        }
      })
      .then(({data}) => data[getWellnessCategoryByIdKey])
  }

  getWellnessMediaCategories(options: QueryOptions = {}): Promise<WellnessMediaCategory[]> {
    return client
      .query({
        ...options,
        query: getWellnessMediaCategoriesQuery
      })
      .then(({data}) => data[getWellnessMediaCategoriesKey])
  }

  getWellnessMediaByCategoryId(categoryId: string): Promise<WellnessMedia[]> {
    return client
      .query({
        query: getMediaByCategoryIdQuery,
        variables: {
          categoryId
        }
      })
      .then(({data}) => data[getMediaByCategoryIdKey])
  }

  getWellnessTagsByCategoryId(categoryId: string): Promise<string[]> {
    return client
      .query({
        query: getWellnessCategoryTagsQuery,
        variables: {
          categoryId
        }
      })
      .then(({data}) => data[getWellnessMediaCategoryTagsKey])
  }

  getMediaByTagName(tagName: string[], categoryId: string): Promise<WellnessMedia[]> {
    return client
      .query({
        query: getMediaByTagNameKeyQuery,
        variables: {
          tagName,
          categoryId
        }
      })
      .then(({data}) => data[getMediaByTagNameKey])
  }

  getMediaList(queryData?: MediaListQueryInterface): Promise<WellnessMediaList> {
    return client
      .query({
        query: wellnessMediaList,
        variables: {
          offset: queryData?.offset,
          limit: queryData?.limit,
          categoryId: queryData?.categoryId,
          tags: queryData?.tags || []
        }
      })
      .then(({data}) => data[wellnessMediaListQueryKey])
  }

  getVideoSourceByVideoId(videoId: string): Promise<string> {
    return client
      .query({
        query: getWellnessVideoSourceQuery,
        variables: {
          videoId
        }
      })
      .then(({data}) => data[getWellnessVideoSourceByIdKey])
  }

  getBanner() {
    return client
      .query({
        query: getBannerQuery
      })
      .then(({data}) => data[getBannerKey])
  }

  createMediaRental(variables) {
    return client
      .mutate({
        mutation: createMediaRental,
        variables
      })
      .then(({data}) => data[createMediaRentalKey])
  }

  wellnessMediaRentals(options: QueryOptions = {}) {
    return client
      .query({
        query: wellnessMediaRentalsQuery,
        ...options,
        variables: {
          userId: authentication.user().uuid,
          expiryStatus: "ACTIVE"
        }
      })
      .then(({data}) => data[wellnessMediaRentalsKey])
  }

  completeMediaRental(variables) {
    return client
      .mutate({
        mutation: completeMediaRental,
        variables
      })
      .then(({data}) => data[completeMediaRentalKey])
  }
}

const service = new WellnessService({errorHandlers: []})

export default service
